import * as React from 'react';
import styled from 'styled-components';
import rtl from 'styled-components-rtl';
import Button from 'components/@client/Button';
import { useMediaQuery } from '@material-ui/core';
import { addToCartBulkProductAdd, AllQuoteComponents } from 'api/transform';
import {
  useAuthentication,
  useBulkProductAddToCart,
  useFetch,
  useRouter,
} from 'hooks';
import { FormattedMessage } from 'react-intl';
import { METHODS } from 'api/client';
import { Message } from 'i18n';
import { BulkAddToCartProduct } from 'types/products';
import { sortBy } from 'utils/lodash.utils';
import SectionTitle from 'components/@client/SectionTitle/SectionTitle';
import {
  MAX_TABLET_RESOLUTION,
  MOBILE_RESOLUTION,
  MOBILE_TABLET_RESOLUTION,
  MIN_TABLET_RESOLUTION,
} from 'styles/sharedStyle';
import { ReactComponent as Checkmark } from 'components/@client/form-components/pick.svg';
import Modal, { ModalAction } from 'components/@client/Modal';
import type { Setter, Value } from 'types/common.types';
import { LogsAPI } from 'api/methods';
import { userLogData } from 'mockData/userLogData';
import { ClientRouter } from 'router/routes';
import FilterChip from './FilterChip';
import ChipsModal from './ChipsModal';

interface Props {
  productsCount: number | null;
  selectedRoom: number | null;
  setSelectedRoom: Setter<number | null>;
  getProducts: (room?: number | null) => void;
  propertyId: string;
}

const NUM_OF_DESKTOP_CHIPS = 4;
const Controller: React.FC<Props> = ({
  productsCount,
  selectedRoom,
  setSelectedRoom,
  getProducts,
  propertyId,
}) => {
  const onSuccess = React.useCallback(
    (rooms: Value[] | null) => {
      rooms?.unshift({ id: 0, name: 'All' });
      setSelectedRoom(rooms?.[0]?.id ?? null);
    },
    [setSelectedRoom]
  );
  const { data: rooms } = useFetch<Value[], Value[]>({
    initialUrl: 'api/rooms/',
    initialData: [],
    config: { params: { property: propertyId } },
    transform: data => sortBy(data, 'id'),
    onSuccess,
  });
  const [isChipModalOpen, setIsChipModalOpen] = React.useState(false);
  const isMobile = useMediaQuery('(max-width: 800px)');
  const firstFourRooms = [...(rooms ?? [])].slice(0, NUM_OF_DESKTOP_CHIPS);
  const isOtherAreasSelected =
    !!selectedRoom &&
    !firstFourRooms.map(room => room.id).includes(selectedRoom);
  const [isOpen, setIsOpen] = React.useState(false);
  const { token } = useAuthentication();
  const { history } = useRouter();
  const toggleModal = React.useCallback(() => {
    setIsChipModalOpen(false);
  }, []);

  const filterByRoom = React.useCallback(
    (roomId: number) => {
      LogsAPI.postUserLogs({
        area: userLogData.eventArea.myProperties,
        section: userLogData.eventSection.propertyPage,
        name: userLogData.eventName.roomsFilter,
        path: userLogData.eventPath.appPropertiesIdPath,
        metadata: {
          property_id: propertyId,
          room_name: rooms?.find(e => e.id === roomId)?.name,
        },
      });
      setSelectedRoom(roomId);

      if (roomId === 0) {
        getProducts(null);
      } else {
        getProducts(roomId);
      }
    },
    [getProducts, setSelectedRoom, propertyId, rooms]
  );

  const { isLoading, data, callFetch } = useFetch({
    initialUrl: `/api/quote_components/`,
    skipOnStart: true,
    config: {
      method: METHODS.GET,
      params: selectedRoom === 0 ? '' : { room: selectedRoom ?? undefined },
    },
    transform: AllQuoteComponents,
  });
  React.useEffect(() => {
    if (selectedRoom !== null)
      callFetch({
        url: `/api/quote_components/`,
        params: {
          room: selectedRoom || undefined,
          property: propertyId,
        },
      });
  }, [callFetch, propertyId, selectedRoom]);

  const {
    callBulkProductAddCart,
    isLoading: isLoaderAddToCartBulk,
  } = useBulkProductAddToCart();

  const addedToCart = React.useCallback(() => {
    if (data) {
      const cartBulkAdded = data?.map(item => {
        const cartProductObject: BulkAddToCartProduct = {
          product: item.product_id,
          quantity: 1,
          checkoutQuantity: 1,
        };
        return cartProductObject;
      });
      if (cartBulkAdded?.length) {
        callBulkProductAddCart({
          data: addToCartBulkProductAdd(cartBulkAdded),
        });
        setIsOpen(true);
      }
    }
  }, [data, callBulkProductAddCart]);

  const actions: ModalAction[] = [
    {
      label: <FormattedMessage id={Message.CART_KEEP_SHOPPING_BUTTON} />,
      onClick: () => setIsOpen(false),
      variant: 'secondary',
    },
    {
      label: <FormattedMessage id={Message.CART_CHECKOUT_BUTTON} />,
      onClick: () =>
        token
          ? history.push(ClientRouter.CART)
          : history.push(`${ClientRouter.ADDITIONAL_INFO}?redirect=CART`),
      variant: 'primary',
    },
  ];

  return (
    <Container>
      <SectionTitle
        label={
          <FormattedMessage
            id={Message.PROPERTY_PRODUCTS_TITLE}
            values={{
              count: `(${productsCount})`,
              span: (chunks: string) => (
                <span style={{ color: '#ff5241' }}>{chunks}</span>
              ),
            }}
          />
        }
      />
      <Wrapper>
        <>
          <FiltersContainer>
            {(isMobile ? rooms : firstFourRooms)?.map(room => {
              return (
                <FilterChip
                  key={room.id}
                  label={room.name}
                  onClick={() => filterByRoom(room.id)}
                  selected={selectedRoom === room.id}
                />
              );
            })}
            {!isMobile && (rooms as Value[]).length > NUM_OF_DESKTOP_CHIPS && (
              <FilterChip
                label={`+${
                  (rooms as Value[]).length - NUM_OF_DESKTOP_CHIPS
                } More areas `}
                onClick={() => setIsChipModalOpen(true)}
                selected={isOtherAreasSelected}
                moreAreas={true}
              />
            )}
          </FiltersContainer>
          {!isMobile && (
            <ChipsModal
              isModalOpen={isChipModalOpen}
              rooms={rooms}
              selectedRoom={selectedRoom}
              setSelectedRoom={setSelectedRoom}
              close={toggleModal}
              filterByRoom={filterByRoom}
            />
          )}
          <ButtonContainer>
            <Button
              isLoading={isLoaderAddToCartBulk}
              onClick={addedToCart}
              className="buyNow-button"
              disabled={isLoading}
            >
              {/* <ShoppingCart /> */}
              <svg
                className="cart-icon"
                width="23"
                height="21"
                viewBox="0 0 23 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.015625 1.0867C0.186704 0.704971 0.48367 0.58879 0.883929 0.59211C2.05243 0.608707 3.22093 0.59875 4.38943 0.59875C4.87038 0.59875 5.08343 0.771358 5.18995 1.24935C5.3352 1.90991 5.47723 2.57047 5.62571 3.25426H5.90331C11.0228 3.25426 16.1422 3.25426 21.2649 3.25426C21.5651 3.25426 21.8459 3.31733 21.9589 3.62271C22.0267 3.79864 22.0331 4.031 21.9815 4.21688C21.5167 5.93301 21.0357 7.64581 20.558 9.35529C20.1964 10.6565 19.8349 11.961 19.4702 13.2622C19.3411 13.7203 19.1409 13.8763 18.6793 13.8763C14.864 13.8763 11.0486 13.8763 7.2332 13.8763C6.92978 13.8763 6.67801 13.956 6.53598 14.2514C6.33262 14.6663 6.60054 15.1509 7.05244 15.2007C7.13637 15.2107 7.22352 15.2074 7.31068 15.2074C11.1035 15.2074 14.8962 15.2074 18.6922 15.2074C18.9957 15.2074 19.2474 15.2904 19.3895 15.5825C19.6025 16.014 19.3088 16.5119 18.8278 16.5318C18.4986 16.5451 18.1693 16.5318 17.8465 16.5318C18.0789 16.6347 18.3307 16.7077 18.5373 16.8472C18.7471 16.9899 18.9505 17.1758 19.0925 17.3882C19.241 17.6073 19.3507 17.8695 19.4056 18.1284C19.4573 18.3807 19.4508 18.6595 19.4121 18.9151C19.2733 19.8246 18.392 20.5449 17.4882 20.5118C16.4779 20.4752 15.7 19.7317 15.6031 18.6662C15.5289 17.8529 15.984 16.8372 17.2461 16.5418H8.67285C9.94787 16.8306 10.4224 17.8928 10.3191 18.7359C10.19 19.7748 9.37653 20.5217 8.39525 20.5151C7.38814 20.5051 6.5844 19.745 6.48433 18.6728C6.40041 17.7965 6.92978 16.8073 8.09828 16.5019C7.67865 16.5019 7.2558 16.5351 6.84263 16.4953C6.04534 16.4156 5.36748 15.7451 5.22223 14.9485C5.06406 14.0854 5.43527 13.2323 6.15832 12.8174C6.21319 12.7842 6.27129 12.7543 6.34553 12.7112C5.57084 9.12294 4.79291 5.53136 4.01499 1.92982H2.00078C1.62635 1.92982 1.25514 1.91323 0.883929 1.93314C0.48367 1.95638 0.186704 1.8236 0.015625 1.44187V1.0867ZM20.5418 4.59861H5.91622L7.63347 12.5353H18.3372C19.0731 9.8864 19.8059 7.2508 20.5418 4.59861ZM9.05051 18.5201C9.05051 18.1583 8.75677 17.8596 8.40493 17.8596C8.05309 17.8596 7.76258 18.1616 7.76258 18.5234C7.76258 18.8853 8.05632 19.184 8.40816 19.184C8.76 19.184 9.05051 18.8819 9.05051 18.5201ZM18.1726 18.5201C18.1726 18.1583 17.8756 17.8596 17.5237 17.8629C17.1719 17.8629 16.8814 18.1683 16.8846 18.5301C16.8846 18.8919 17.1816 19.1906 17.5334 19.1873C17.8853 19.1873 18.1758 18.8819 18.1726 18.5201Z"
                  fill="white"
                  fillOpacity="0.9"
                />
              </svg>

              <FormattedMessage id={Message.TOUR_ADD_ENTIRE_ROOM_TO_CART} />
            </Button>
          </ButtonContainer>
        </>
      </Wrapper>
      {isOpen && (
        <ConfirmModal
          className="confirm-modal"
          onClose={() => setIsOpen(false)}
          actions={actions}
          open={isOpen}
        >
          <Content>
            {`${data?.length}`} <FormattedMessage id={Message.PRODUCTS} />
          </Content>
          <Content>
            <Checkmark className="checkmark" />
            <FormattedMessage id={Message.ADDED_TO_CART} />
          </Content>
        </ConfirmModal>
      )}
    </Container>
  );
};

export default Controller;

const Container = styled.div`
  margin: 0 160px;
  min-height: 100px;

  @media ${MOBILE_TABLET_RESOLUTION} {
    margin: 0 24px;
  }
  .section-title-wrapper {
    margin: 48px 0 24px 0 !important;
    @media (max-width: 1600px) and (min-width: 800px) {
      padding: 0 0;
    }
  }
  .section-title-label {
    position: relative;
    padding: 0 24px 0 0;
    ${rtl`
        padding: 0;
    `}
  }
`;
const Wrapper = styled.div`
  justify-content: space-between;
  @media (min-width: 800px) {
    display: flex;
  }
`;

const ButtonContainer = styled.div`
  text-align: end;
  top: -42px;
  @media ${MAX_TABLET_RESOLUTION} {
    text-align: center;
  }

  @media ${MIN_TABLET_RESOLUTION} {
    & .buyNow-button {
      width: 201px;
    }
  }

  @media ${MOBILE_TABLET_RESOLUTION} {
    & .buyNow-button {
      text-align: center;
      margin-top: 24px;
      padding: 12px 36px !important;
      font-size: 16px;
      font-weight: 500;
      border-radius: 15px;
      height: 48px;
      text-align: right;
      min-width: 235px;
      background-color: #282929;
    }
  }
  @media ${MOBILE_RESOLUTION} {
    width: 100%;
  }
  .cart-icon {
    ${rtl` margin-right: 10px;`}
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  overflow-x: auto;
  @media (min-width: 1024px) and (max-width: 1200px) {
    max-width: 45vw;
  }
  @media (max-width: 1200px) and (min-width: 800px) {
    max-width: 35vw;
  }
  ${({ theme }) => theme.utils.scrollbar}
  padding-top: 6px;
  & > div:not(:last-of-type) {
    ${rtl`
        margin-right: 16px;
    `}
    @media (max-width: 800px) {
      ${rtl`
        margin-right: 8px;
    `}
    }
    @media (max-width: 800px) {
      display: flex;
    }
  }
`;
const ConfirmModal = styled(Modal)`
  & .MuiDialogActions-root {
    @media ${MOBILE_RESOLUTION} {
      display: block;
      text-align: center;
      & > button {
        margin: 10px !important;
      }
    }
    & > button {
      min-width: 150px;
    }
  }
`;

const Content = styled.p`
  text-align: center;
  img {
    max-width: 80px;
    max-height: 80px;
    margin-bottom: 10px;
  }
  margin-bottom: 20px;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.black0};
  svg {
    path {
      fill: ${({ theme }) => theme.colors.primary.lightMobile};
    }
    ${rtl`
      margin-right: 10px;
    `}
  }
`;
